import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

/**
 * Reusable ECharts lifecycle hook.
 *
 * Fixes the Angular re-init bug: the original component called
 * `echarts.init(...)` on every `handleCounselling` run, which stacks multiple
 * instances on the same DOM node and triggers "there is a chart instance
 * already initialized on the dom" warnings. Here we init once, reuse the same
 * instance via `setOption`, handle window resize, and dispose on unmount.
 *
 * @param option  the ECharts option object (re-applied whenever it changes)
 * @param active  when false the chart is not rendered (e.g. bar chart is only
 *                shown for user type 5); the instance is disposed while hidden
 */
export function useECharts(
  option: echarts.EChartsCoreOption | null,
  active = true
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!active || !el || !option) {
      // Tear down when hidden/empty so we never leak an instance.
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
      return;
    }

    // Reuse the existing instance if one is already attached to this node.
    let chart = echarts.getInstanceByDom(el);
    if (!chart) {
      chart = echarts.init(el);
    }
    chartRef.current = chart;
    chart.setOption(option, true);

    const handleResize = () => chart?.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [option, active]);

  // Dispose once on unmount.
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, []);

  return containerRef;
}
